import { useEffect, useState } from 'react';

import { useAiResultContext } from './AiResultProvider';

import { type AiResultNotification } from '@/api/ai-gateway/aiHub';

/**
 * Returns the AI result pushed for a given `correlationId`, or `undefined` until it arrives. Pass the
 * same correlationId you sent with the request; the hook resets when it changes.
 *
 * @example
 * const result = useAiResult(correlationId);
 * if (result?.success) show(result.content);
 */
export function useAiResult(
    correlationId: string | null | undefined,
): AiResultNotification | undefined {
    const { subscribe, getCached } = useAiResultContext();
    const [result, setResult] = useState<AiResultNotification>();

    useEffect(() => {
        if (!correlationId) {
            setResult(undefined);
            return;
        }

        // Prime from cache in case the response already arrived before this hook mounted/subscribed.
        setResult(getCached(correlationId));

        return subscribe((notification) => {
            if (notification.correlationId === correlationId) {
                setResult(notification);
            }
        });
    }, [correlationId, subscribe, getCached]);

    return result;
}

/**
 * Lower-level escape hatch: run a callback for every AI result push (any correlationId/feature).
 * Prefer {@link useAiResult} when you're waiting on one specific request.
 */
export function useAiResultListener(onResult: (notification: AiResultNotification) => void): void {
    const { subscribe } = useAiResultContext();

    useEffect(() => subscribe(onResult), [subscribe, onResult]);
}
